import { deriveTurn, historyBlock } from './agentActivity'
import { formatBytes, relativeTime } from './format'
import { resolvePath } from './messageTree'
import type {
  ChatRead,
  ChatSummary,
  DashboardRead,
  DashboardSummary,
  MessageRead,
  PaginatedMessages,
} from '@/types/api'
import type {
  ActivityItem,
  AgentPart,
  ChatDetail,
  ChatMessage,
  FileArtifact,
} from '@/types/chat'
import type { DashboardDetail, DashboardVisuals } from '@/types/dashboard'

/**
 * Backend DTO → UI view-model adapters (Phase 7). The backend persists
 * messages as Anthropic-style content_blocks (`additionalProperties: true`
 * in the spec), so every read here is defensive; the mapping mirrors
 * useChatStream's block→AgentPart derivation so history and live turns
 * render identically.
 */

const str = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined
const num = (v: unknown): number | undefined =>
  typeof v === 'number' ? v : undefined

export function toChatSummary(chat: ChatRead): ChatSummary {
  return {
    id: chat.chat_id,
    title: chat.title,
    starred: chat.starred,
    projectId: chat.project_id ?? null,
    updated_at:
      chat.last_message_at ?? chat.updated_at ?? chat.created_at ?? '',
  }
}

export function toDashboardSummary(dash: DashboardRead): DashboardSummary {
  return {
    id: dash.dashboard_id,
    title: dash.name,
    description: dash.description ?? '',
    updated_at: dash.updated_at ?? dash.created_at ?? '',
  }
}

export function toDashboardDetail(
  dash: DashboardRead & DashboardVisuals,
): DashboardDetail {
  return {
    id: dash.dashboard_id,
    title: dash.name,
    subtitle: dash.subtitle ?? dash.description ?? '',
    refreshed:
      dash.refreshed ??
      relativeTime(
        dash.updated_at ?? dash.created_at ?? new Date(0).toISOString(),
      ),
    queries: dash.queries ?? [],
    tiles: dash.tiles ?? [],
    line: dash.line ?? null,
    bar: dash.bar ?? null,
  }
}

// ── messages ────────────────────────────────────────────────────────────────

/** `/mnt/outputs/a001_chart.chartjs.json` → kind "json", name basename. */
function fileKindOf(name: string, mime?: string): string {
  if (name.includes('.')) return (name.split('.').pop() ?? 'file').toLowerCase()
  if (mime?.includes('/')) return mime.split('/')[1]
  return 'file'
}

/** "XLSX · generated · 33 KB" / "PDF · 2.2 MB" — the files-list caption.
 * `generated` distinguishes agent-produced artifacts (msg.artifacts, always
 * under /mnt/outputs/) from the user's own uploads (file_attachments_metadata). */
function fileMeta(kind: string, sizeBytes?: number, generated = false): string {
  const bits = [kind.toUpperCase()]
  if (generated) bits.push('generated')
  if (sizeBytes !== undefined) bits.push(formatBytes(sizeBytes))
  return bits.join(' · ')
}

function toFileArtifact(
  raw: Record<string, unknown>,
  chatId: string,
  generated = false,
): FileArtifact {
  const path = str(raw.path) ?? str(raw.file_uri) ?? ''
  const name =
    str(raw.name) ??
    str(raw.filename) ??
    (path ? (path.split('/').pop() ?? path) : 'file')
  // raw.kind is a literal artifact-type marker ("file"), not a format — the
  // real extension is raw.format (agent artifacts) or derived from the name/
  // mime (user uploads, which carry neither `kind` nor `format`).
  const mime = str(raw.mime) ?? str(raw.content_type) ?? str(raw.mime_type)
  const kind = str(raw.format) ?? fileKindOf(name, mime)
  return {
    id: str(raw.id) ?? str(raw.artifact_id) ?? str(raw.file_id) ?? path ?? name,
    name,
    kind,
    path,
    chatId,
    meta: str(raw.meta) ?? fileMeta(kind, num(raw.size_bytes), generated),
    preview: Array.isArray(raw.preview)
      ? (raw.preview as string[][])
      : undefined,
  }
}

/** `message_meta.artifacts` / `MessageRead.artifacts` → file cards. Generated
 * files only carry path/filename/format/size — the preview rows are fetched
 * from the artifact endpoint when a card is expanded. */
export function filesFromArtifacts(
  raw: unknown,
  chatId?: string,
): FileArtifact[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const files = raw
    .filter(
      (a): a is Record<string, unknown> => Boolean(a) && typeof a === 'object',
    )
    .map((a) => toFileArtifact(a, chatId ?? '', true))
    .filter((f) => Boolean(f.path))
  return files.length > 0 ? files : undefined
}

/** "1.4 s · 5 rows · duolift_dev" from a tool_trace's flat metric fields. */
function traceMeta(raw: Record<string, unknown>): string | undefined {
  const bits: string[] = []
  const ms = num(raw.duration_ms)
  if (ms !== undefined) bits.push(`${ms / 1000} s`)
  const rows = num(raw.rows)
  if (rows !== undefined) bits.push(`${rows} rows`)
  const source = str(raw.source)
  if (source) bits.push(source)
  return bits.length ? bits.join(' · ') : undefined
}

/** `tool_traces` — the backend's curated one-line-per-step summary. Used only
 * when the persisted content_blocks carry no thinking/tool blocks of their own
 * (those derive far richer rows via the shared stream mapping). */
function traceActivity(msg: MessageRead): ActivityItem[] {
  return (msg.tool_traces ?? [])
    .map((t, i): ActivityItem => {
      const label = str(t.text) ?? str(t.label) ?? str(t.tool_name) ?? ''
      return {
        kind: 'tool',
        id: `trace-${i}`,
        toolName: str(t.tool_name) ?? 'tool',
        label,
        status: 'complete',
        result: str(t.meta) ?? traceMeta(t),
      }
    })
    .filter((item) => item.kind === 'tool' && item.label)
}

function assistantParts(msg: MessageRead, chatId: string): AgentPart[] {
  // Same mapping the live stream uses — a turn looks identical before and
  // after the refetch that replaces it with its persisted form.
  const { parts } = deriveTurn((msg.content_blocks ?? []).map(historyBlock))

  if (!parts.some((p) => p.kind === 'activity')) {
    const items = traceActivity(msg)
    if (items.length > 0) {
      parts.unshift({
        kind: 'activity',
        items,
        summary: `Worked · ${items.length} step${items.length === 1 ? '' : 's'}`,
        running: false,
      })
    }
  }
  for (const raw of msg.artifacts ?? []) {
    parts.push({ kind: 'file', file: toFileArtifact(raw, chatId, true) })
  }
  return parts
}

export function userText(msg: MessageRead): string {
  return (msg.content_blocks ?? [])
    .map((b) => (b.type === 'text' || b.type === undefined ? str(b.text) : ''))
    .filter(Boolean)
    .join('\n\n')
}

/** Variant-nav info for one node on the resolved path — undefined/single-entry
 * siblingIds means no `‹ x/x ›` widget is needed for this message. */
export interface SiblingInfo {
  siblingIds: string[]
  siblingIndex: number
}

export function toChatMessage(
  msg: MessageRead,
  chatId: string,
  siblings?: SiblingInfo,
): ChatMessage {
  if (msg.sender === 'user') {
    const files = (msg.file_attachments_metadata ?? []).map((m) =>
      toFileArtifact(m, chatId),
    )
    return {
      id: msg.response_id,
      role: 'user',
      text: userText(msg),
      files: files.length ? files : undefined,
      siblingIds: siblings?.siblingIds,
      siblingIndex: siblings?.siblingIndex,
    }
  }
  return {
    id: msg.response_id,
    role: 'assistant',
    parts: assistantParts(msg, chatId),
    followups: msg.suggested_questions ?? undefined,
    siblingIds: siblings?.siblingIds,
    siblingIndex: siblings?.siblingIndex,
    usedThinking: (msg.content_blocks ?? []).some((b) => b.type === 'thinking'),
    instruction: msg.instruction ?? undefined,
    parentUserMessageId: msg.parent_response_id ?? undefined,
  }
}

export function toChatDetail(
  chatId: string,
  page: PaginatedMessages,
  overrides?: Map<string, string>,
): ChatDetail {
  // Regenerate makes this a tree (sibling assistant messages sharing one
  // parent_response_id) — resolvePath walks it into the single linear path
  // the thread renders, anchored at the server-persisted active_leaf_id.
  const path = resolvePath(page.messages, page.active_leaf_id, overrides)
  return {
    id: chatId,
    title: page.title,
    starred: page.starred,
    projectId: page.project_id ?? null,
    source: '', // no data-source field in the backend chat API
    messages: path.map(({ message, siblingIds, siblingIndex }) =>
      toChatMessage(message, chatId, { siblingIds, siblingIndex }),
    ),
  }
}
